<template>
  <form v-if="form" @submit.prevent="submitForm">
    <EstimateDocumentEditor
      v-model="form"
      :customers="customers"
      :representatives="representatives"
      :buildings="buildings"
      :classifications="classifications"
      :item-name-suggestions="itemNameSuggestions"
      :preview="preview"
      :error-messages="allErrorMessages"
      :is-submitting="isSubmitting"
      :is-generating-preview="isGeneratingPreview"
      :submit-label="submitLabel"
      :show-layout-selector="showLayoutSelector"
      @cancel="emit('cancel')"
      @preview="openPreview"
      @customer-changed="customerChanged"
      @classification-changed="classificationChanged"
      @add-section="addSection"
      @add-line="addLine($event.lines, $event.lineType)"
      @remove-section="removeSection"
      @remove-line="removeLine($event.lines, $event.line)"
      @select-frequent-work="openFrequentWorkModal"
      @search-item-name-suggestions="searchItemNameSuggestions"
    />

    <EstimateFrequentWorkModal
      v-if="ESTIMATE_FREQUENT_WORKS_ENABLED"
      :open="frequentWorkModalOpen"
      :options="ESTIMATE_FREQUENT_WORKS"
      @close="closeFrequentWorkModal"
      @select="addFrequentWorks"
    />

    <EstimatePdfPreviewModal ref="pdfPreviewModal" />
  </form>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import {
  getBusinessClassifications,
  getEstimateItemNameSuggestions,
  getSelectBoxBuildings,
  getSelectBoxCustomerRepresentatives,
  getSelectBoxCustomers,
  getUser
} from '@/api/axios';
import EstimateDocumentEditor from '@/components/estimates/editor/EstimateDocumentEditor.vue';
import EstimateFrequentWorkModal from '@/components/estimates/editor/EstimateFrequentWorkModal.vue';
import EstimatePdfPreviewModal from '@/components/estimates/EstimatePdfPreviewModal.vue';
import {
  ESTIMATE_FREQUENT_WORKS_ENABLED,
  ESTIMATE_FREQUENT_WORKS,
  frequentWorkLineAttributes
} from '@/utils/estimateFrequentWorks';
import { extractApiErrors } from '@/utils/estimates';
import {
  calculateEstimatePreview,
  calculateLineAmount,
  hasAtMostDecimalPlaces,
  mergeEstimatePortraitRemarks,
  normalizeDecimalInputValue,
  toAmountOnlyDiscount
} from '@/utils/estimateEditor';
import { createEstimatePdf } from '@/utils/estimatePdf';

const props = defineProps({
  initialEstimate: {
    type: Object,
    default: null
  },
  defaultOutputLayoutType: {
    type: String,
    default: 'portrait_temporary',
    validator: value => [
      'portrait_annual',
      'portrait_temporary',
      'landscape_detailed'
    ].includes(value)
  },
  showLayoutSelector: {
    type: Boolean,
    default: true
  },
  isSubmitting: {
    type: Boolean,
    default: false
  },
  errorMessages: {
    type: Array,
    default: () => []
  },
  submitLabel: {
    type: String,
    default: '保存'
  }
});

const emit = defineEmits(['submit', 'cancel']);
const store = useStore();
const form = ref(null);
const customers = ref([]);
const representatives = ref([]);
const buildings = ref([]);
const classifications = ref([]);
const itemNameSuggestions = ref([]);
const masterErrorMessages = ref([]);
const clientErrorMessages = ref([]);
const frequentWorkModalOpen = ref(false);
const frequentWorkTargetLines = ref(null);
const pdfPreviewModal = ref(null);
const isGeneratingPreview = ref(false);
let localKeySequence = 0;
let itemNameSuggestionSearchTimer;
let itemNameSuggestionRequestId = 0;

const localKey = prefix => `${prefix}-${Date.now()}-${localKeySequence++}`;
const estimateTypeForLayout = layoutType =>
  layoutType === 'portrait_annual' ? 'annual' : 'temporary';
const todayString = () => {
  const now = new Date();
  const localDate = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 10);
};
const addMonths = (dateString, months) => {
  const date = new Date(`${dateString}T00:00:00`);
  const originalDay = date.getDate();
  date.setDate(1);
  date.setMonth(date.getMonth() + months);
  const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  date.setDate(Math.min(originalDay, lastDay));
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 10);
};

const newLine = overrides => ({
  local_key: localKey('line'),
  id: undefined,
  sort_no: 0,
  line_type: 'detail',
  m_business_classification_id: '',
  m_estimate_subcategory_id: '',
  item_nm: '',
  part_nm: '',
  specification: '',
  notes: '',
  quantity: '',
  quantity_decimal_places: null,
  unit_nm: '',
  unit_price: '',
  amount: 0,
  manual_amount_flg: false,
  manual_amount_reason: '',
  tax_type: 'taxable',
  work_frequency: '',
  annual_count: '',
  service_count: '',
  _destroy: false,
  ...(overrides || {})
});

const newSection = overrides => ({
  local_key: localKey('section'),
  id: undefined,
  sort_no: 0,
  section_nm: '',
  subcategory_nm: '',
  m_business_classification_id: '',
  m_estimate_subcategory_id: '',
  notes: '',
  quantity: 1,
  quantity_decimal_places: 0,
  unit_nm: '式',
  _destroy: false,
  lines: [newLine()],
  ...(overrides || {})
});

const defaultEstimate = () => {
  const issueDate = todayString();
  const isLandscape = props.defaultOutputLayoutType === 'landscape_detailed';
  return {
    lock_version: undefined,
    m_customer_id: '',
    m_customer_representative_id: '',
    m_building_id: '',
    recipient_customer_nm: '',
    building_nm: '',
    title: '',
    work_location: '',
    issue_date: issueDate,
    valid_until: addMonths(issueDate, 6),
    output_layout_type: props.defaultOutputLayoutType,
    estimate_type: estimateTypeForLayout(props.defaultOutputLayoutType),
    default_language_type: 'japanese',
    seal_flg: true,
    tax_rate: 10,
    tax_rounding_type: 'floor',
    payment_terms: '',
    delivery_terms: '',
    general_notes: '',
    internal_notes: '',
    created_by_user: store.state.userName ? { name: store.state.userName } : null,
    approved_by_user: null,
    created_stamp_name: store.state.userName || '',
    approved_stamp_name: '',
    created_stamp_image_url: '',
    approved_stamp_image_url: '',
    sections: isLandscape ? [newSection()] : [],
    unsectioned_lines: isLandscape ? [] : [newLine()]
  };
};

const quantityInputValue = (value, decimalPlaces) => {
  if (value === '' || value == null) return '';
  if (
    decimalPlaces === null ||
    decimalPlaces === undefined ||
    decimalPlaces === '' ||
    !Number.isInteger(Number(decimalPlaces))
  ) return value;

  return Number(value).toFixed(Number(decimalPlaces));
};
const normalizeLine = line => toAmountOnlyDiscount(newLine({
  ...line,
  quantity: quantityInputValue(line.quantity, line.quantity_decimal_places),
  unit_price: normalizeDecimalInputValue(line.unit_price),
  m_business_classification_id:
    line.m_business_classification_id || line.business_classification?.id || '',
  m_estimate_subcategory_id:
    line.m_estimate_subcategory_id || line.estimate_subcategory?.id || '',
  manual_amount_flg: Boolean(line.manual_amount_flg),
  _destroy: false
}));
const normalizeSection = section => newSection({
  ...section,
  quantity: quantityInputValue(section.quantity, section.quantity_decimal_places),
  subcategory_nm:
    section.subcategory_nm || section.estimate_subcategory?.subcategory_nm || '',
  m_business_classification_id:
    section.m_business_classification_id || section.business_classification?.id || '',
  m_estimate_subcategory_id:
    section.m_estimate_subcategory_id || section.estimate_subcategory?.id || '',
  _destroy: false,
  lines: (section.lines || []).map(normalizeLine)
});
const normalizeEstimate = source => {
  if (!source) return defaultEstimate();
  const outputLayoutType = source.output_layout_type || props.defaultOutputLayoutType;
  const estimateType = ['portrait_annual', 'portrait_temporary'].includes(outputLayoutType)
    ? estimateTypeForLayout(outputLayoutType)
    : source.estimate_type;
  const estimate = {
    ...defaultEstimate(),
    ...source,
    output_layout_type: outputLayoutType,
    estimate_type: ['annual', 'temporary'].includes(estimateType) ? estimateType : 'temporary',
    m_customer_id: source.m_customer_id || source.customer?.id || '',
    m_customer_representative_id:
      source.m_customer_representative_id || source.customer_representative?.id || '',
    m_building_id: source.m_building_id || source.building?.id || '',
    recipient_customer_nm:
      source.recipient_customer_nm || source.recipient?.customer_nm || source.customer?.customer_nm || '',
    building_nm:
      source.building_nm || source.building_snapshot?.building_nm || source.building?.building_nm || '',
    sections: (source.sections || []).map(normalizeSection),
    unsectioned_lines: (source.unsectioned_lines || []).map(normalizeLine)
  };
  return outputLayoutType === 'landscape_detailed'
    ? estimate
    : mergeEstimatePortraitRemarks(estimate);
};

watch(
  () => props.initialEstimate,
  value => { form.value = normalizeEstimate(value); },
  { immediate: true }
);

watch(
  () => form.value?.default_language_type,
  languageType => {
    (form.value?.sections || []).forEach(section => {
      if (languageType === 'english' && section.unit_nm === '式') section.unit_nm = 'Lot';
      if (languageType === 'japanese' && section.unit_nm === 'Lot') section.unit_nm = '式';
    });
  }
);

const preview = computed(() => calculateEstimatePreview(form.value));
const selectedCustomer = computed(() => customers.value.find(customer =>
  String(customer.id) === String(form.value?.m_customer_id)
));
const selectedRepresentative = computed(() => representatives.value.find(representative =>
  String(representative.id) === String(form.value?.m_customer_representative_id)
));
const selectedBuilding = computed(() => buildings.value.find(building =>
  String(building.id) === String(form.value?.m_building_id)
));
const joinedAddress = record => [record?.prefecture, record?.city, record?.other_address]
  .filter(Boolean)
  .join('');
const previewEstimate = computed(() => {
  const amounts = preview.value;
  const customer = selectedCustomer.value;
  const representative = selectedRepresentative.value;
  const building = selectedBuilding.value;
  const activeLines = lines => (lines || [])
    .filter(line => !line._destroy)
    .map(line => ({ ...line, amount: calculateLineAmount(line) }));

  return {
    ...form.value,
    estimate_no: form.value.estimate_no || '未採番',
    approval_status: form.value.approval_status || 'draft',
    subtotal_amount: amounts.subtotal,
    discount_amount: amounts.discount,
    taxable_amount: amounts.taxable,
    tax_amount: amounts.tax,
    total_amount: amounts.total,
    recipient: {
      customer_nm:
        customer?.customer_nm || form.value.recipient_customer_nm || form.value.recipient?.customer_nm || '',
      department_nm: representative?.in_charge_department_nm || '',
      person_nm: [representative?.in_charge_last_nm, representative?.in_charge_first_nm]
        .filter(Boolean)
        .join(' '),
      honorific: form.value.recipient?.honorific || '御中',
      postal_code: customer?.postal_code || '',
      address: joinedAddress(customer),
      tel_num: customer?.tel_num || customer?.phone_num || ''
    },
    building_snapshot: {
      building_nm:
        building?.building_nm || form.value.building_nm || form.value.building_snapshot?.building_nm || '',
      postal_code: building?.postal_code || '',
      address: joinedAddress(building)
    },
    sections: (form.value.sections || [])
      .filter(section => !section._destroy)
      .map(section => ({ ...section, lines: activeLines(section.lines) })),
    unsectioned_lines: activeLines(form.value.unsectioned_lines)
  };
});
const allErrorMessages = computed(() => [
  ...props.errorMessages,
  ...masterErrorMessages.value,
  ...clientErrorMessages.value
]);
const customerChanged = () => {
  if (form.value.m_customer_id) form.value.recipient_customer_nm = '';
  form.value.m_customer_representative_id = '';
  form.value.m_building_id = '';
  form.value.building_nm = '';
};
const classificationChanged = record => {
  // 小分類は画面上で使用しない。業務区分変更時に非表示の旧関連だけを残さない。
  record.m_estimate_subcategory_id = '';
};

const addSection = () => form.value.sections.push(newSection({
  unit_nm: form.value.default_language_type === 'english' ? 'Lot' : '式'
}));
const addLine = (lines, lineType = 'detail') => lines.push(newLine({ line_type: lineType }));
const removeRecord = (records, record) => {
  const index = records.indexOf(record);
  if (index < 0) return;
  if (record.id) record._destroy = true;
  else records.splice(index, 1);
};
const removeSection = section => {
  (section.lines || []).forEach(line => {
    if (!line.id) return;
    line._destroy = true;
    if (!form.value.unsectioned_lines.includes(line)) {
      form.value.unsectioned_lines.push(line);
    }
  });
  section.lines = [];
  removeRecord(form.value.sections, section);
};
const removeLine = (lines, line) => {
  removeRecord(lines, line);
};
const openFrequentWorkModal = lines => {
  frequentWorkTargetLines.value = lines;
  frequentWorkModalOpen.value = true;
};
const closeFrequentWorkModal = () => {
  frequentWorkModalOpen.value = false;
  frequentWorkTargetLines.value = null;
};
const openPreview = async () => {
  if (isGeneratingPreview.value) return;

  try {
    isGeneratingPreview.value = true;
    clientErrorMessages.value = [];
    const pdf = await createEstimatePdf(previewEstimate.value);
    await pdfPreviewModal.value?.show(pdf);
  } catch (error) {
    clientErrorMessages.value = [error.message || 'PDFプレビューを作成できませんでした。'];
  } finally {
    isGeneratingPreview.value = false;
  }
};
const isEmptyDetailLine = line => (
  !line.id &&
  !line._destroy &&
  line.line_type === 'detail' &&
  [
    line.item_nm,
    line.part_nm,
    line.specification,
    line.notes,
    line.quantity,
    line.unit_nm,
    line.unit_price
  ].every(value => value === '' || value == null)
);
const addFrequentWorks = works => {
  const targetLines = frequentWorkTargetLines.value;
  if (!Array.isArray(targetLines)) {
    closeFrequentWorkModal();
    return;
  }
  const reusableLines = targetLines.filter(isEmptyDetailLine);
  works.forEach(work => {
    const attributes = frequentWorkLineAttributes(work, form.value.default_language_type);
    const reusableLine = reusableLines.shift();
    if (reusableLine) Object.assign(reusableLine, attributes);
    else targetLines.push(newLine(attributes));
  });
  closeFrequentWorkModal();
};

const serializeLine = (line, sortNo) => ({
  id: line.id,
  sort_no: sortNo,
  line_type: line.line_type,
  m_business_classification_id: line.m_business_classification_id || null,
  m_estimate_subcategory_id: line.m_estimate_subcategory_id || null,
  item_nm: line.item_nm,
  part_nm: line.part_nm,
  specification: line.specification,
  notes: line.notes,
  quantity: line.quantity === '' ? null : line.quantity,
  quantity_decimal_places: line.quantity === '' ? null : line.quantity_decimal_places,
  unit_nm: line.unit_nm,
  unit_price: line.unit_price === '' ? null : line.unit_price,
  amount: calculateLineAmount(line),
  manual_amount_flg: line.manual_amount_flg,
  manual_amount_reason: line.manual_amount_reason,
  tax_type: line.tax_type,
  work_frequency: line.work_frequency,
  annual_count: line.annual_count === '' ? null : line.annual_count,
  service_count: line.service_count === '' ? null : line.service_count,
  _destroy: Boolean(line._destroy)
});
const serializeSection = (section, sortNo) => ({
  id: section.id,
  sort_no: sortNo,
  section_nm: section.section_nm,
  subcategory_nm: section.subcategory_nm,
  m_business_classification_id: section.m_business_classification_id || null,
  m_estimate_subcategory_id: section.m_estimate_subcategory_id || null,
  notes: section.notes,
  quantity: section.quantity === '' ? null : section.quantity,
  quantity_decimal_places: section.quantity === '' ? null : section.quantity_decimal_places,
  unit_nm: section.unit_nm,
  _destroy: Boolean(section._destroy),
  lines: section.lines.map((line, index) => serializeLine(line, index + 1))
});
const validateBeforeSubmit = () => {
  const errors = [];
  if (!String(form.value.title || '').trim()) errors.push('件名を入力してください。');
  if (!form.value.issue_date) errors.push('発行日を入力してください。');
  if (!['annual', 'temporary'].includes(form.value.estimate_type)) {
    errors.push('見積種別は臨時または年間を選択してください。');
  }
  if (
    form.value.issue_date && form.value.valid_until &&
    form.value.valid_until < form.value.issue_date
  ) {
    errors.push('有効期限は発行日以降の日付を指定してください。');
  }
  if (Number(form.value.tax_rate) < 0 || Number(form.value.tax_rate) > 100) {
    errors.push('消費税率は0～100の範囲で入力してください。');
  }

  const validateLine = (line, label) => {
    if (line._destroy) return;
    if (line.line_type === 'detail' && !String(line.item_nm || '').trim()) {
      errors.push(`${label}の品名・作業名を入力してください。`);
    }
    if (line.manual_amount_flg && !String(line.manual_amount_reason || '').trim()) {
      errors.push(`${label}の金額手入力理由を入力してください。`);
    }
    ['quantity', 'unit_price', 'amount', 'annual_count', 'service_count'].forEach(field => {
      const value = line[field];
      if (value !== '' && value != null && Number(value) < 0) {
        errors.push(`${label}の数値項目は0以上で入力してください。`);
      }
    });
    if (!hasAtMostDecimalPlaces(line.quantity, 2)) {
      errors.push(`${label}の数量は小数点以下2桁以内で入力してください。`);
    }
  };

  form.value.sections.forEach((section, sectionIndex) => {
    if (section._destroy) return;
    const sectionLabel = `工事項目${sectionIndex + 1}`;
    if (!String(section.section_nm || '').trim()) {
      errors.push(`${sectionLabel}の名称を入力してください。`);
    }
    if (section.quantity === '' || section.quantity == null || Number(section.quantity) < 0) {
      errors.push(`${sectionLabel}の数量は0以上で入力してください。`);
    }
    if (!hasAtMostDecimalPlaces(section.quantity, 2)) {
      errors.push(`${sectionLabel}の数量は小数点以下2桁以内で入力してください。`);
    }
    if (!String(section.unit_nm || '').trim()) {
      errors.push(`${sectionLabel}の単位を入力してください。`);
    }
    section.lines.forEach((line, lineIndex) =>
      validateLine(line, `${sectionLabel} 明細${lineIndex + 1}`)
    );
  });
  form.value.unsectioned_lines.forEach((line, lineIndex) =>
    validateLine(line, `全体明細${lineIndex + 1}`)
  );

  clientErrorMessages.value = [...new Set(errors)];
  return clientErrorMessages.value.length === 0;
};
const submitForm = () => {
  if (!validateBeforeSubmit()) {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    return;
  }
  emit('submit', {
    lock_version: form.value.lock_version,
    m_customer_id: form.value.m_customer_id || null,
    m_customer_representative_id: form.value.m_customer_representative_id || null,
    m_building_id: form.value.m_building_id || null,
    recipient_customer_nm: form.value.recipient_customer_nm,
    building_nm: form.value.building_nm,
    title: form.value.title,
    work_location: form.value.work_location,
    issue_date: form.value.issue_date,
    valid_until: form.value.valid_until,
    output_layout_type: form.value.output_layout_type,
    estimate_type: form.value.estimate_type,
    default_language_type: form.value.default_language_type,
    seal_flg: Boolean(form.value.seal_flg),
    tax_rate: form.value.tax_rate,
    tax_rounding_type: form.value.tax_rounding_type,
    payment_terms: form.value.payment_terms,
    delivery_terms: form.value.delivery_terms,
    general_notes: form.value.general_notes,
    internal_notes: form.value.internal_notes,
    sections: form.value.sections.map((section, index) => serializeSection(section, index + 1)),
    unsectioned_lines: form.value.unsectioned_lines.map((line, index) => serializeLine(line, index + 1))
  });
};

const searchItemNameSuggestions = query => {
  clearTimeout(itemNameSuggestionSearchTimer);
  const requestId = ++itemNameSuggestionRequestId;
  itemNameSuggestionSearchTimer = setTimeout(async () => {
    try {
      const response = await getEstimateItemNameSuggestions({ q: query || '' });
      if (requestId === itemNameSuggestionRequestId) {
        itemNameSuggestions.value = response.data.item_name_suggestions || [];
      }
    } catch (_error) {
      if (requestId === itemNameSuggestionRequestId) {
        itemNameSuggestions.value = [];
      }
    }
  }, 200);
};

onBeforeUnmount(() => clearTimeout(itemNameSuggestionSearchTimer));

onMounted(async () => {
  // 過去入力候補は任意情報のため、取得失敗でフォーム全体をエラーにしない。
  const suggestionRequests = Promise.allSettled([
    getEstimateItemNameSuggestions()
  ]);

  try {
    const [
      customerResponse,
      representativeResponse,
      buildingResponse,
      classificationResponse
    ] = await Promise.all([
      getSelectBoxCustomers(),
      getSelectBoxCustomerRepresentatives(),
      getSelectBoxBuildings(),
      getBusinessClassifications({ deleted_status: 'active' })
    ]);
    customers.value = customerResponse.data || [];
    representatives.value = representativeResponse.data || [];
    buildings.value = buildingResponse.data || [];
    classifications.value = classificationResponse.data || [];
  } catch (error) {
    masterErrorMessages.value = ['選択肢の読み込みに失敗しました。', ...extractApiErrors(error)];
  }

  const [itemNameSuggestionResult] = await suggestionRequests;
  if (itemNameSuggestionResult.status === 'fulfilled') {
    itemNameSuggestions.value = itemNameSuggestionResult.value.data.item_name_suggestions || [];
  }

  // 保存前の新規見積でも、ログインユーザーに登録された社員印画像を表示する。
  const currentUserId = localStorage.getItem('userId');
  if (!form.value?.id && currentUserId) {
    try {
      const response = await getUser(currentUserId);
      form.value.created_stamp_image_url = response.data.employee_seal_image_url || '';
    } catch (_error) {
      // 画像取得に失敗した場合は、誤った社員印を表示しないよう空欄のままにする。
    }
  }
});
</script>
